import { initials } from "../../lib/avatar";
import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { LogOut, User as UserIcon, Settings, ShieldCheck, Building2, BookOpen, Rocket, LifeBuoy } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useProductTour } from "../../contexts/ProductTourContext";
import RoleBadge from "../users/RoleBadge";
import { can } from "../../lib/permissions"; // V2-T1

export default function UserMenu() {
  const { user, logout } = useAuth();
  const { start: demarrerVisite } = useProductTour();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    toast.success("Vous avez été déconnecté");
    navigate("/login", { replace: true });
  };

  if (!user) return null;

  return (
    <DropdownMenu>
      {/*
       * Mesuré à 320 px : 36 × 36. Ce bouton n'utilise pas la variante `icon`
       * du composant Button, il portait donc la taille de l'avatar et
       * échappait au socle des 44 px posé en août. La CIBLE passe à 44 sur
       * mobile ; l'avatar garde ses 36 px — on agrandit la zone cliquable, pas
       * le dessin. Le commentaire reste HORS du trigger : `asChild` n'accepte
       * qu'un seul enfant, un commentaire JSX à l'intérieur le ferait planter.
       */}
      <DropdownMenuTrigger asChild>
        <button
          data-testid="user-menu-trigger"
          className="outline-none focus:ring-2 focus:ring-brand/40 rounded-full shrink-0 h-11 w-11 md:h-9 md:w-9 inline-flex items-center justify-center"
          aria-label="Menu utilisateur"
        >
          <Avatar className="h-9 w-9 border border-border">
            <AvatarImage src={user.avatar_url || undefined} alt={user.full_name} />
            <AvatarFallback className="bg-brand/10 text-brand text-xs font-semibold">
              {/* « U » et non « ? » : repli propre a cet ecran, conserve au balayage. */}
              {initials(user.full_name, "U")}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>
          <div className="flex flex-col gap-1">
            <p data-testid="user-menu-name" className="text-sm font-semibold text-foreground truncate">
              {user.full_name}
            </p>
            <p data-testid="user-menu-email" className="text-xs text-muted-foreground truncate">
              {user.email}
            </p>
            <div data-testid="user-menu-role" className="mt-1">
              <RoleBadge role={user.role} withTooltip />
            </div>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* Profil — visible pour tous */}
        <DropdownMenuItem
          data-testid="user-menu-profile"
          onClick={() => navigate("/profile")}
          className="cursor-pointer"
        >
          <UserIcon className="mr-2 h-4 w-4" /> Mon profil
        </DropdownMenuItem>

        {/*
         * Le bouton « Aide » quitte la barre sous `sm` : six commandes à 44 px
         * n'y tiennent pas à 320 px. Ses deux entrées atterrissent ici, où il y
         * a de la place — le menu utilisateur, lui, reste visible sur mobile.
         * Masquées à partir de `sm`, où le bouton d'aide est de retour : on ne
         * veut pas les proposer deux fois sur le même écran.
         */}
        <DropdownMenuItem
          data-testid="user-menu-tour"
          onClick={demarrerVisite}
          className="cursor-pointer sm:hidden"
        >
          <Rocket className="mr-2 h-4 w-4" /> Visite guidée
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="user-menu-help"
          onClick={() => navigate("/help/roles")}
          className="cursor-pointer sm:hidden"
        >
          <LifeBuoy className="mr-2 h-4 w-4" /> Centre d'aide
        </DropdownMenuItem>

        {/* Paramètres entreprise — owner uniquement (V2-T1) */}
        {can(user, 'menu:company_settings') && (
          <DropdownMenuItem
            data-testid="user-menu-settings"
            onClick={() => navigate("/settings/company")}
            className="cursor-pointer"
          >
            <Building2 className="mr-2 h-4 w-4" /> Paramètres entreprise
          </DropdownMenuItem>
        )}

        {/* Facturation — owner uniquement (V2-T1) */}
        {can(user, 'menu:billing') && (
          <DropdownMenuItem
            data-testid="user-menu-billing"
            onClick={() => navigate("/settings/billing")}
            className="cursor-pointer"
          >
            <Settings className="mr-2 h-4 w-4" /> Facturation
          </DropdownMenuItem>
        )}

        {/* Variables de paie — owner uniquement (V2-T1) */}
        {can(user, 'menu:payroll_variables') && (
          <DropdownMenuItem
            data-testid="user-menu-payroll"
            onClick={() => navigate("/settings/payroll-variables")}
            className="cursor-pointer"
          >
            <Settings className="mr-2 h-4 w-4" /> Variables de paie
          </DropdownMenuItem>
        )}

        {/* Préférences notifications — visible pour tous */}
        <DropdownMenuItem
          data-testid="user-menu-notifications-prefs"
          onClick={() => navigate("/settings/notifications")}
          className="cursor-pointer"
        >
          <Settings className="mr-2 h-4 w-4" /> Préférences notifications
        </DropdownMenuItem>

        {/* RGPD — owner uniquement (V2-T1) */}
        {can(user, 'menu:rgpd') && (
          <DropdownMenuItem
            data-testid="user-menu-rgpd"
            onClick={() => navigate("/settings/rgpd")}
            className="cursor-pointer"
          >
            <ShieldCheck className="mr-2 h-4 w-4" /> Mes données &amp; RGPD
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {/* Comprendre les rôles — visible pour tous */}
        <DropdownMenuItem
          data-testid="user-menu-help-roles"
          onClick={() => navigate("/help/roles")}
          className="cursor-pointer"
        >
          <BookOpen className="mr-2 h-4 w-4" /> Comprendre les rôles
        </DropdownMenuItem>

        {/* Comparer les plans — owner + manager seulement (V2-T1/T8) */}
        {can(user, 'menu:compare_plans') && (
          <DropdownMenuItem
            data-testid="user-menu-help-plans"
            onClick={() => navigate("/help/plans")}
            className="cursor-pointer"
          >
            <BookOpen className="mr-2 h-4 w-4" /> Comparer les plans
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          data-testid="user-menu-logout"
          onClick={handleLogout}
          className="cursor-pointer text-danger focus:text-danger"
        >
          <LogOut className="mr-2 h-4 w-4" /> Se déconnecter
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
